// Pure helpers for the curated key-signal model (Domain's SignalCatalog.KeySignalTypes /
// RiskSignalStates) — split out from components/RiskSignalInsights.tsx so this logic is unit-testable
// without rendering (see __tests__/riskSignals.test.ts), matching this project's "logic lives in utils/,
// components stay thin" convention.
import type { getStrings } from "../i18n/strings";
import type { PublicRisk } from "../types/dto";

type Strings = ReturnType<typeof getStrings>;

const FALLBACK_SIGNAL_NAMES: Record<string, string> = {
  Tor: "Tor Exit Node",
  // tor-classification-v1: a Tor node that is not an exit. Labelled separately from Tor above, because
  // the two are different findings — a middle or guard relay carries no exit traffic at all.
  TorRelay: "Tor Relay (non-exit)",
  OpenProxy: "Open Proxy",
  Proxy: "Proxy",
  ResidentialProxy: "Residential Proxy",
  Vpn: "VPN",
  Datacenter: "Hosting/Datacenter",
  AttackSource: "Recent Abuse",
  AbuseHistory: "Abuse History",
  MultipleBlacklists: "Multiple Blacklists",
  ExtensiveBlacklisting: "Extensive Blacklisting",
  Malware: "Malware",
  Compromised: "Compromised Host",
  InternetScanner: "Internet Scanner",
  SSHBruteForce: "SSH Brute Force",
  Spam: "Spam Source",
};

const FALLBACK_STATE_NAMES: Record<string, string> = {
  detected: "Detected",
  not_detected: "Not detected",
  unknown: "Unknown",
  not_supported: "Unavailable",
};

export function signalLabel(signalType: string, strings: Strings): string {
  return strings.riskSignalNames?.[signalType] ?? FALLBACK_SIGNAL_NAMES[signalType] ?? signalType;
}

export function signalStateLabel(state: string, strings: Strings): string {
  return strings.riskSignalStateNames?.[state] ?? FALLBACK_STATE_NAMES[state] ?? state;
}

// Spam joins this group with risk-v4, which is the first version able to emit it — ProxyCheck's
// attack_history categories now resolve to real per-behavior signals rather than one aggregate boolean.
const REPUTATION_SIGNALS = ["AttackSource", "AbuseHistory", "MultipleBlacklists", "ExtensiveBlacklisting", "Malware", "Compromised", "Spam"];
const NETWORK_SIGNALS = ["Datacenter"];

export interface WhyThisScoreGroups {
  strongSignals: string[];
  // risk-v4.9: keySignals entries RiskExplanationEngine marked "contextualized" or "historical" — split
  // out of strongSignals so a recognized public DNS resolver's dampened AttackSource/AbuseHistory/
  // InternetScanner no longer sits under the "Strong signals" heading, which is exactly the confusing-
  // badge problem this feature exists to fix. Absent signalExplanations (a pre-feature share snapshot)
  // means every keySignals entry stays in strongSignals, unchanged from before this field existed.
  contextualizedSignals: string[];
  // risk-v4.13: keySignals entries RiskExplanationEngine marked "network_identity" (Tor/Proxy/Vpn/...) —
  // split out of strongSignals for exactly the same reason contextualizedSignals is: without this, a
  // confirmed Tor exit with zero attack/abuse/compromise evidence still listed under "Strong signals"
  // here even after the rest of the page stopped calling it an active threat, reproducing the same
  // contradiction one level down.
  identitySignals: string[];
  reputationSignals: string[];
  networkSignals: string[];
  noEvidenceSignals: string[];
}

function isContextualizedKeySignal(risk: PublicRisk, signalType: string): boolean {
  const displayState = risk.signalExplanations?.[signalType]?.displayState;
  return displayState === "contextualized" || displayState === "historical";
}

function isNetworkIdentityKeySignal(risk: PublicRisk, signalType: string): boolean {
  return risk.signalExplanations?.[signalType]?.displayState === "network_identity";
}

// Groups the curated signals into Strong signals / Contextualized / Anonymous network type / Reputation /
// Network type / No evidence detected for the "Why this score?" disclosure. "Strong"/"Contextualized"/
// "Anonymous network type" always win: a signal already surfaced as a key signal never also appears under
// Reputation/Network, and a Datacenter=not_detected never shows up under "No evidence detected"
// (network-type absence isn't a risk fact worth listing there). "unknown" and "not_supported" states
// never appear in any group — only a genuine detected/not_detected answer does.
export function groupSignalsForWhyThisScore(risk: PublicRisk): WhyThisScoreGroups {
  const strongSignals = risk.keySignals.filter(
    (type) => !isContextualizedKeySignal(risk, type) && !isNetworkIdentityKeySignal(risk, type),
  );
  const contextualizedSignals = risk.keySignals.filter((type) => isContextualizedKeySignal(risk, type));
  const identitySignals = risk.keySignals.filter((type) => isNetworkIdentityKeySignal(risk, type));
  const alreadyListed = (type: string) =>
    strongSignals.includes(type) || contextualizedSignals.includes(type) || identitySignals.includes(type);

  const reputationSignals = REPUTATION_SIGNALS.filter(
    (type) => risk.signalStates[type] === "detected" && !alreadyListed(type),
  );
  const networkSignals = NETWORK_SIGNALS.filter(
    (type) => risk.signalStates[type] === "detected" && !alreadyListed(type),
  );
  const noEvidenceSignals = Object.entries(risk.signalStates)
    .filter(([type, state]) => state === "not_detected" && !alreadyListed(type) && type !== "Datacenter")
    .map(([type]) => type);

  return { strongSignals, contextualizedSignals, identitySignals, reputationSignals, networkSignals, noEvidenceSignals };
}
